import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { AuthProvider } from './lib/auth';
import { CallProvider } from './lib/calls';
import { SoftphoneProvider } from './lib/softphone';
import { Boundary } from './components/Boundary';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry a rejection the server meant; only transport failures.
        const status = (error as { status?: number }).status ?? 0;
        if (status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      * Outside everything, so a failure in any provider is caught too. A
      * boundary mounted inside the thing that throws catches nothing, and the
      * result is the white page this exists to replace.
      */}
    <Boundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <CallProvider>
              {/*
                * The softphone's provider was written and never mounted, so
                * `useSoftphone` fell through to its "off" fallback everywhere
                * and the browser phone could not work at all — no token was
                * ever fetched and no device was ever registered.
                *
                * Inside AuthProvider because it needs to know who is signed
                * in, and above App so the on-call bar outlives a route change.
                */}
              <SoftphoneProvider>
                <App />
              </SoftphoneProvider>
            </CallProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Boundary>
  </StrictMode>,
);
